import { useState } from "react";
import { Loader2, PlugZap } from "lucide-react";
import { Connector, Credentials } from "../../shared/ConnectorCredentialsForm";
import {
  canRunDraftTest,
  isPmsTestSupported,
} from "../../../lib/pmsCredentialsTest";
import type { TestCredentialsResponse } from "../../../types/connectivity";

interface PMSCredentialsFormProps {
  connectors: Connector[];
  selectedConnector: Connector | null;
  credentials: Credentials;
  onConnectorSelect: (connector: Connector) => void;
  onCredentialChange: (key: string, value: string | boolean) => void;
  hotelCode?: string;
  testLoading?: boolean;
  testResult?: TestCredentialsResponse | null;
  testError?: string | null;
  onTestConnection?: () => void;
}

export function PMSCredentialsForm({
  connectors,
  selectedConnector,
  credentials,
  onConnectorSelect,
  onCredentialChange,
  hotelCode,
  testLoading = false,
  testResult = null,
  testError = null,
  onTestConnection,
}: PMSCredentialsFormProps) {
  const [urlError, setUrlError] = useState<string>("");

  // Function to validate URL
  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      setUrlError("");
      return true;
    } catch (e) {
      setUrlError("Please enter a valid URL (e.g., https://example.com)");
      return false;
    }
  };

  // Handle URL change with validation
  const handleUrlChange = (value: string) => {
    if (value) {
      validateUrl(value);
    } else {
      setUrlError("Connector URL is required");
    }
    onCredentialChange("base_url", value);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="form-label">Connector Provider</label>
        <select
          value={selectedConnector?.code || ""}
          onChange={(e) => {
            const connector = connectors.find((c) => c.code === e.target.value);
            if (connector) {
              onConnectorSelect(connector);
            }
          }}
          className="form-input"
          required
        >
          <option value="">Select Connector Provider</option>
          {connectors.map((connector) => (
            <option key={connector.id} value={connector.code}>
              {connector.name}
            </option>
          ))}
        </select>
      </div>

      {selectedConnector && (
        <>
          {/* For cloudified connectors, only show Hotel ID and Is Primary */}
          {selectedConnector.cloudified ? (
            <>
              {/* Hotel Identifier field */}
              <div>
                <label className="form-label">Hotel Identifier</label>
                <input
                  type="text"
                  value={
                    typeof credentials["connector_hotel_id"] === "string"
                      ? credentials["connector_hotel_id"]
                      : ""
                  }
                  onChange={(e) =>
                    onCredentialChange("connector_hotel_id", e.target.value)
                  }
                  className="form-input"
                  placeholder="hotel123"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Your unique hotel identifier in the PMS system
                </p>
              </div>

              {/* Is Primary checkbox */}
              <div className="flex items-center">
                <input
                  id="is_primary"
                  type="checkbox"
                  checked={credentials["is_primary"] === true}
                  onChange={(e) =>
                    onCredentialChange("is_primary", e.target.checked)
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_primary"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Set as Primary Connector
                </label>
              </div>
              <div className="mt-1 text-sm">
                <p className="text-gray-500">
                  Primary connector will be used as the default for this hotel.
                </p>
                <p className="mt-1 text-amber-600">
                  Note: Only one PMS connector can be primary for a hotel.
                  Setting this as primary will remove primary status from any
                  other PMS connector.
                </p>
              </div>

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  This is a cloudified connector. When you proceed, an SFTP
                  account will be created for you to securely transfer data.
                </p>
                <p className="mt-2 text-sm text-blue-700">
                  Your SFTP account details will be available in the hotel
                  details page after setup is complete.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Connector URL field */}
              <div>
                <label className="form-label">Connector URL</label>
                <input
                  type="text"
                  value={
                    typeof credentials["base_url"] === "string"
                      ? credentials["base_url"]
                      : ""
                  }
                  onChange={(e) => handleUrlChange(e.target.value)}
                  className={`form-input ${urlError ? "border-red-500" : ""}`}
                  placeholder="https://api.example.com"
                  required
                />
                {urlError && (
                  <p className="mt-1 text-sm text-red-500">{urlError}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  The base URL for the connector API
                </p>
              </div>

              {/* Hotel Identifier field */}
              <div>
                <label className="form-label">Hotel Identifier</label>
                <input
                  type="text"
                  value={
                    typeof credentials["connector_hotel_id"] === "string"
                      ? credentials["connector_hotel_id"]
                      : ""
                  }
                  onChange={(e) =>
                    onCredentialChange("connector_hotel_id", e.target.value)
                  }
                  className="form-input"
                  placeholder="hotel123"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Your unique hotel identifier in the PMS system
                </p>
              </div>

              {/* Is Primary checkbox */}
              <div className="flex items-center">
                <input
                  id="is_primary"
                  type="checkbox"
                  checked={credentials["is_primary"] === true}
                  onChange={(e) =>
                    onCredentialChange("is_primary", e.target.checked)
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_primary"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Set as Primary Connector
                </label>
              </div>
              <div className="mt-1 text-sm">
                <p className="text-gray-500">
                  Primary connector will be used as the default for this hotel.
                </p>
                <p className="mt-1 text-amber-600">
                  Note: Only one PMS connector can be primary for a hotel.
                  Setting this as primary will remove primary status from any
                  other PMS connector.
                </p>
              </div>

              {/* Message for cloudified connectors */}
              {selectedConnector.cloudified && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Cloudified Connector:</strong> This connector uses
                    SFTP for data exchange. No additional credentials are
                    required. An SFTP account will be automatically created for
                    you.
                  </p>
                </div>
              )}

              {/* Original credential fields */}
              {!selectedConnector.cloudified &&
                selectedConnector.credentials &&
                selectedConnector.credentials.map((field) => (
                  <div key={field.key}>
                    <label className="form-label">{field.key}</label>
                    <input
                      type={
                        field.key.toLowerCase().includes("password")
                          ? "password"
                          : "text"
                      }
                      value={
                        typeof credentials[field.key] === "string"
                          ? (credentials[field.key] as string)
                          : ""
                      }
                      onChange={(e) =>
                        onCredentialChange(field.key, e.target.value)
                      }
                      className="form-input"
                      placeholder={field.example}
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {field.description}
                    </p>
                  </div>
                ))}

              {/* Test connection — draft mode, Thais only for now */}
              {!selectedConnector.cloudified && onTestConnection && (
                <div className="border-t border-gray-200 pt-4 mt-2 space-y-3">
                  {isPmsTestSupported(selectedConnector.code) ? (
                    <button
                      type="button"
                      onClick={onTestConnection}
                      disabled={
                        testLoading ||
                        !hotelCode ||
                        !canRunDraftTest(selectedConnector, credentials)
                      }
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {testLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Testing…
                        </>
                      ) : (
                        <>
                          <PlugZap className="h-4 w-4 mr-2" />
                          Test connection
                        </>
                      )}
                    </button>
                  ) : (
                    <div>
                      <button
                        type="button"
                        disabled
                        title="Coming soon for this PMS"
                        className="inline-flex items-center px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed"
                      >
                        <PlugZap className="h-4 w-4 mr-2" />
                        Test connection
                      </button>
                      <p className="mt-1 text-xs text-gray-500">
                        Coming soon for this PMS
                      </p>
                    </div>
                  )}

                  {testError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700">{testError}</p>
                    </div>
                  )}

                  {testResult && !testError && (
                    <div
                      className={`p-3 border rounded-md ${
                        testResult.success
                          ? "bg-green-50 border-green-200"
                          : "bg-red-50 border-red-200"
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          testResult.success ? "text-green-800" : "text-red-700"
                        }`}
                      >
                        {testResult.message}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
